// ---------------------------------------------------------------------------
// Accessify AI Service Layer
// ---------------------------------------------------------------------------
// Two modes:
//  1. Proxy mode (recommended): Widget sends requests to Accessify API proxy
//     with a public siteKey. The proxy adds the operator's encrypted API key.
//  2. Direct mode (legacy/dev): Widget sends requests directly to OpenRouter
//     with an API key in the client code (insecure, for development only).
// ---------------------------------------------------------------------------

import type { AIService } from '../types';
import { pageLocation } from '../preview-url';

interface AIServiceConfig {
  siteKey?: string;
  proxyUrl?: string;
  apiKey?: string; // Legacy: direct OpenRouter key
  textModel?: string;
  visionModel?: string;
}

const DEFAULT_PROXY = 'https://api.goaccessify.de';
const OPENROUTER_BASE = 'https://openrouter.ai/api/v1';
const DEFAULTS = {
  textModel: 'google/gemini-2.0-flash-001',
  visionModel: 'google/gemini-2.0-flash-001',
};

// ---------------------------------------------------------------------------
// Factory
// ---------------------------------------------------------------------------

export function createAIService(config: AIServiceConfig): AIService {
  // If an explicit API key is provided, prefer direct mode (dev/testing)
  if (config.apiKey) {
    return createDirectService(config);
  }
  if (config.siteKey) {
    return createProxyService(config.siteKey, config.proxyUrl);
  }
  return createDirectService(config);
}

// ---------------------------------------------------------------------------
// Proxy mode — recommended for production
// ---------------------------------------------------------------------------

function createProxyService(siteKey: string, proxyUrl?: string): AIService {
  const base = proxyUrl || DEFAULT_PROXY;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Site-Key': siteKey,
  };

  return {
    async simplifyText(text: string, level = 'einfache', lang = 'de'): Promise<string> {
      // Try cached result first via cache/query
      const blockId = `b-${simpleHash(text)}`;
      // Variant key MUST match what crawl + persist use: just the level string.
      // Previously this was `de-${level}` which caused cache misses and duplicate
      // AI calls on every page load (the crawl stores under 'einfache', but
      // cache/query was looking for 'de-einfache' → permanent miss → AI loop).
      const variant = level;
      try {
        const cacheRes = await fetch(`${base}/v1/cache/query`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            pageUrl: typeof window !== 'undefined' ? pageLocation().href : '',
            feature: 'simplify',
            variant,
            blocks: [{ id: blockId, text }],
          }),
        });
        if (cacheRes.ok) {
          const cacheData = await cacheRes.json();
          if (cacheData.cached?.[blockId]) return cacheData.cached[blockId];
        }
      } catch { /* cache miss, fall through */ }

      // Try cache/process (generates + caches server-side)
      try {
        const processRes = await fetch(`${base}/v1/cache/process`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            pageUrl: typeof window !== 'undefined' ? pageLocation().href : '',
            feature: 'simplify',
            variant,
            blocks: [{ id: blockId, text }],
          }),
        });
        if (processRes.ok) {
          const processData = await processRes.json();
          if (processData.results?.[blockId]) return processData.results[blockId];
        }
      } catch { /* fall through to direct proxy call */ }

      // Fallback: direct simplify endpoint (no caching)
      const res = await fetch(`${base}/v1/ai/simplify`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ text, level, lang }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
        if (res.status === 429) throw new RateLimitError(err.error || 'Rate limit reached');
        throw new Error(err.error || `AI proxy error: ${res.status}`);
      }
      const data = await res.json();
      return data.result || '';
    },

    async generateAltText(imageUrl: string, context?: string, lang?: string): Promise<string> {
      const res = await fetch(`${base}/v1/ai/alt-text`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ imageUrl, context, lang }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
        if (res.status === 429) throw new RateLimitError(err.error || 'Rate limit reached');
        throw new Error(err.error || `AI proxy error: ${res.status}`);
      }
      const data = await res.json();
      return data.result || '';
    },

    async describeImage(imageUrl: string, lang?: string): Promise<string> {
      const res = await fetch(`${base}/v1/ai/describe-image`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ imageUrl, lang }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
        if (res.status === 429) throw new RateLimitError(err.error || 'Rate limit reached');
        throw new Error(err.error || `AI proxy error: ${res.status}`);
      }
      const data = await res.json();
      return data.result || '';
    },
  };
}

// Simple string hash for cache block IDs
function simpleHash(str: string): string {
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) + hash) + str.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash).toString(36);
}

// ---------------------------------------------------------------------------
// Direct mode — legacy / development only
// ---------------------------------------------------------------------------

/** Extract text from an OpenRouter response, handling reasoning models where content may be null */
function extractContent(data: any): string {
  const msg = data.choices?.[0]?.message;
  if (msg?.content && typeof msg.content === 'string') return msg.content.trim();
  // Reasoning models (e.g. nvidia/nemotron) put output in reasoning field
  if (msg?.reasoning && typeof msg.reasoning === 'string') return msg.reasoning.trim();
  if (Array.isArray(msg?.reasoning_details)) {
    for (const detail of msg.reasoning_details) {
      if (detail?.text) return detail.text.trim();
    }
  }
  return '';
}

function createDirectService(config: AIServiceConfig): AIService {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(config.apiKey ? { Authorization: `Bearer ${config.apiKey}` } : {}),
    'HTTP-Referer': typeof window !== 'undefined' ? window.location.origin : '',
    'X-Title': 'Accessify Widget',
  };

  return {
    async simplifyText(text: string, level: string = 'einfache', lang: string = 'de'): Promise<string> {
      const systemPrompt = lang.startsWith('de')
        ? getGermanSimplificationPrompt(level as 'einfache' | 'leichte')
        : getEnglishSimplificationPrompt();

      const response = await fetch(`${OPENROUTER_BASE}/chat/completions`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          model: config.textModel || DEFAULTS.textModel,
          messages: [
            { role: 'system', content: systemPrompt },
            { role: 'user', content: text },
          ],
          stream: false,
          max_tokens: 2000,
        }),
      });

      if (!response.ok) {
        if (response.status === 429) throw new RateLimitError('Rate limit reached. Please try again later.');
        throw new Error(`AI service error: ${response.status}`);
      }

      const data = await response.json();
      return extractContent(data);
    },

    async generateAltText(imageUrl: string, context?: string, lang?: string): Promise<string> {
      const langInstruction = lang && !lang.startsWith('en')
        ? ` Generate the alt text in ${lang} language.`
        : '';
      const response = await fetch(`${OPENROUTER_BASE}/chat/completions`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          model: config.visionModel || DEFAULTS.visionModel,
          messages: [{
            role: 'user',
            content: [
              {
                type: 'text',
                text: `Generate a concise, descriptive alt text for this image for web accessibility.${langInstruction} ${context ? 'Context: ' + context : ''} Return ONLY the alt text, nothing else. Max 125 characters.`,
              },
              { type: 'image_url', image_url: { url: imageUrl } },
            ],
          }],
          max_tokens: 300,
        }),
      });

      if (!response.ok) {
        if (response.status === 429) throw new RateLimitError('Rate limit reached. Please try again later.');
        throw new Error(`AI vision error: ${response.status}`);
      }

      const data = await response.json();
      return extractContent(data);
    },

    async describeImage(imageUrl: string, lang?: string): Promise<string> {
      const langInstruction = lang && lang.startsWith('de')
        ? ' Beschreibe auf Deutsch.'
        : '';
      const response = await fetch(`${OPENROUTER_BASE}/chat/completions`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          model: config.visionModel || DEFAULTS.visionModel,
          messages: [{
            role: 'user',
            content: [
              {
                type: 'text',
                text: `Describe what is shown in this image. 2-3 sentences, easy to understand.${langInstruction} Return ONLY the description.`,
              },
              { type: 'image_url', image_url: { url: imageUrl } },
            ],
          }],
          max_tokens: 500,
        }),
      });

      if (!response.ok) {
        if (response.status === 429) throw new RateLimitError('Rate limit reached. Please try again later.');
        throw new Error(`AI vision error: ${response.status}`);
      }

      const data = await response.json();
      return extractContent(data);
    },
  };
}

// ---------------------------------------------------------------------------
// Custom error for rate-limit handling
// ---------------------------------------------------------------------------

export class RateLimitError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'RateLimitError';
  }
}

// ---------------------------------------------------------------------------
// System prompts (direct mode only — proxy has its own copies)
// ---------------------------------------------------------------------------

function getGermanSimplificationPrompt(level: 'einfache' | 'leichte'): string {
  if (level === 'leichte') {
    return `Du bist ein Experte für Leichte Sprache (DIN SPEC 33429). Vereinfache den folgenden Text nach diesen Regeln:
- Benutze kurze Sätze (maximal 8 Wörter pro Satz)
- Ein Satz pro Zeile
- Benutze einfache, bekannte Wörter
- Erkläre Fremdwörter und Fachbegriffe
- Benutze aktive Sätze (kein Passiv)
- Benutze keine Abkürzungen
- Benutze keine Metaphern oder bildliche Sprache
- Schreibe in Präsens
- Benutze positive Formulierungen
- Zielgruppe: CEFR A1-A2

STRENGE REGELN — Verstoß = fehlerhaft:
1. ALLE Personen-Namen MÜSSEN im vereinfachten Text vorkommen. Lösche NIEMALS einen Namen.
2. Zitate: IMMER den vollen Namen des Sprechers behalten.
3. Zahlen, Daten, Ergebnisse, Spielstände: Wort für Wort übernehmen.
4. Eigennamen (Vereine, Orte, Firmen): exakt übernehmen.
5. Erfinde NICHTS dazu.

Gib NUR den vereinfachten Text zurück.`;
  }

  return `Du bist ein Experte für Einfache Sprache (DIN ISO 24495-1). Vereinfache den folgenden Text:
- Kurze, klare Sätze (maximal 15 Wörter)
- Vermeide Fachbegriffe oder erkläre sie
- Benutze aktive Formulierungen
- Strukturiere den Text mit klaren Absätzen
- Zielgruppe: CEFR B1-B2

STRENGE REGELN — Verstoß = fehlerhaft:
1. ALLE Personen-Namen MÜSSEN im vereinfachten Text vorkommen. Wenn im Original "Simon Dornieden" steht, MUSS "Simon Dornieden" auch in der Ausgabe stehen. Lösche NIEMALS einen Namen.
2. Zitate: IMMER den vollen Namen des Sprechers behalten (z.B. „Simon Dornieden sagt: …")
3. Zahlen, Daten, Ergebnisse, Spielstände: Wort für Wort übernehmen, NICHT umformulieren
4. Eigennamen (Vereine, Orte, Firmen): exakt übernehmen
5. Adressen, Postleitzahlen: exakt übernehmen
6. Bildunterschriften, Quellenangaben, Fotocredits: NICHT verändern
7. Erfinde NICHTS dazu. Nur vorhandenen Inhalt in einfacherer Sprache wiedergeben.

Gib NUR den vereinfachten Text zurück.`;
}

function getEnglishSimplificationPrompt(): string {
  return `You are a plain language expert. Simplify the following text:
- Use short, clear sentences (max 15 words)
- Avoid jargon, explain technical terms
- Use active voice
- Target reading level: grade 6-8

STRICT RULES — violating any = incorrect output:
1. ALL person names MUST appear in the simplified text. NEVER delete a name.
2. Quotes: ALWAYS keep the full speaker name.
3. Numbers, dates, scores, results: copy verbatim.
4. Proper nouns (teams, places, companies): copy exactly.
5. Do NOT invent details.

Return ONLY the simplified text.`;
}
